import { useEffect, useState } from 'react';
import { FadeInOut } from '@/components/FadeInOut';
import { useCollection } from '@/features/events/contexts/CollectionContext/useCollection';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useLauncherAction } from '@/hooks/useLauncher';
import { PlatformTypes } from '@/types/gql.generated';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
} from '@/ui';
import { useBroadcastReceiver } from '../../extension/hooks/useBroadcast';
import type { DecoratedMagicJob } from '../../types';
import { AuthBody } from './AuthBody';
import { GoogleIntegrationBody } from './GoogleIntegrationBody';
import { useAuthModal } from './store';
import { useHandlePreferredPlatformLaunch } from './useHandlePreferredPlatformLaunch';

interface AuthModalProps {
  onResetAppState?: () => void;
  onChatCreate?: (job: DecoratedMagicJob) => void;
}

export const AuthModal = ({
  onResetAppState,
  onChatCreate,
}: AuthModalProps) => {
  const {
    close,
    isOpen,
    platform,
    reason,
    launch,
    launchParams,
    onSuccess,
    resetOnSuccess,
  } = useAuthModal();
  const initialPlatformCta = useHandlePreferredPlatformLaunch();
  const [platformCta, setPlatformCta] = useState(initialPlatformCta);
  const { trackEvent } = useAnalytics();
  const { base } = useCollection();

  const isGoogleWeblinkPin =
    platform === PlatformTypes.GoogleLink &&
    reason === 'set-preferred-platform';

  useEffect(() => {
    if (isOpen) {
      trackEvent('lead-gate:show', { reason });
    }
  }, [isOpen, reason, trackEvent]);

  // track successful oauth authentications
  useLauncherAction('magic-auth-success', ({ reason }) => {
    trackEvent('lead-gate:success', {
      reason,
    });
  });

  useEffect(() => {
    if (initialPlatformCta) {
      setPlatformCta(initialPlatformCta);
    }
  }, [initialPlatformCta]);

  const handleClose = () => {
    setPlatformCta(null);
    close();
  };

  const handleAuthSuccess = () => {
    if (resetOnSuccess) {
      onResetAppState?.();
    }

    onSuccess?.();

    if (isGoogleWeblinkPin) {
      setPlatformCta(platform);
    } else {
      handleClose();
    }
  };

  const handleAuthComplete = () => {
    trackEvent('lead-gate:success', {
      reason,
    });

    if (base?.job && onChatCreate) {
      onChatCreate(base.job);
    }

    handleAuthSuccess();
  };

  useBroadcastReceiver('_ah_auth_success', handleAuthComplete);

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      size={{ base: 'full', md: 'lg' }}
      onClose={handleClose}
    >
      <ModalOverlay />
      <ModalContent
        layerStyle="magicBackgroundSecondary"
        pb="16"
        pt="6"
        px={{ base: '0', sm: '4', md: '8' }}
      >
        <ModalCloseButton />
        <ModalBody as={FadeInOut} flexDir="column" key={platformCta || 'auth'}>
          {isGoogleWeblinkPin ? (
            <GoogleIntegrationBody onComplete={handleClose} />
          ) : (
            <AuthBody
              launch={launch}
              launchParams={launchParams ?? undefined}
              platform={platform}
              reason={reason}
              resetOnSuccess={resetOnSuccess}
              onComplete={handleAuthComplete}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
